import * as React from 'react';
import {useFormikContext} from 'formik';
import {CourseObject} from 'api/course';
import Row from 'components/Row';
import Column from 'components/Column';
import SelectPrice, {SelectPriceProps} from 'components/SelectPrice';
import Input from 'components/Input';
import CourseBuilderContext from '../../../CourseBuilderContext';
import FormContainer from '../../containers/FormContainer';
import {getCurrencyChar} from 'helpers/get-price-label';
import {DatePicker} from "components/DatePicker";
import {DatePickerDateArg} from "components/DatePicker/DatePicker";

/**
 * The sale form part of settings
 * @return {React.ReactElement}
 * @constructor
 */
export default function SaleForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<CourseObject>>();
  const courseContext = React.useContext(CourseBuilderContext);

  /**
   * Render content
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render dates field
     * @return {JSX.Element}
     */
    function renderDates(): JSX.Element {
      /**
       * Render start date
       * @return {JSX.Element}
       */
      function renderStartDate(): JSX.Element {
        /**
         * Handle date change
         * @param {DatePickerDateArg} date
         */
        function handleChange(date: DatePickerDateArg): void {
          formContext.setFieldValue('sale.dates.startDate', date);
          formContext.submitForm();
        }

        return (
          <Column>
            <Row>
              <Column>
                <DatePicker
                  id="sale.dates.startDate"
                  label="Старт"
                  dateFormat="dd.MM.yyyy в HH:mm"
                  selected={formContext.values.sale?.dates?.startDate}
                  showTimeSelect
                  onChange={handleChange}
                  required
                />
              </Column>
            </Row>
          </Column>
        );
      }

      /**
       * Render over date
       * @return {JSX.Element}
       */
      function renderOverDate(): JSX.Element {
        /**
         * Handle date change
         * @param {DatePickerDateArg} date
         */
        function handleChange(date: DatePickerDateArg): void {
          formContext.setFieldValue('sale.dates.overDate', date);
          formContext.submitForm();
        }

        return (
          <Column>
            <Row>
              <Column>
                <DatePicker
                  id="sale.dates.overDate"
                  label="Финиш"
                  dateFormat="dd.MM.yyyy в HH:mm"
                  selected={formContext.values.sale?.dates?.overDate}
                  showTimeSelect
                  onChange={handleChange}
                />
              </Column>
            </Row>
          </Column>
        );
      }

      return (
        <Row>
          {renderStartDate()}
          {renderOverDate()}
        </Row>
      );
    }

    /**
     * Render discount field
     * @return {JSX.Element}
     */
    function renderDiscount(): JSX.Element {
      /**
       * Handle discount blur
       * @param {React.ChangeEvent<HTMLInputElement>} event
       */
      function handleDiscountBlur(
          event: React.ChangeEvent<HTMLInputElement>,
      ): void {
        formContext.setFieldValue('sale.price', Number(event.target.value));
        formContext.submitForm();
      }

      const inputProps: SelectPriceProps['inputProps'] = {
        defaultValue: courseContext.course?.sale?.price,
        placeholder: 'Укажите новую цену',
        onBlur: handleDiscountBlur,
      };

      const selectProps: SelectPriceProps['selectProps'] = {
        value: {
          label: courseContext.course?.price?.currency ? (
            getCurrencyChar(courseContext.course?.price?.currency)
          ): '',
          value: courseContext.course?.price?.currency || '',
        },
        disabled: true,
      };

      return (
        <Row>
          <Column>
            <Row>
              <Column>
                <SelectPrice
                  label="Новая цена"
                  inputProps={inputProps}
                  selectProps={selectProps}
                  required
                />
              </Column>
            </Row>
          </Column>
        </Row>
      );
    }

    /**
     * Render limit field
     * @return {JSX.Element}
     */
    function renderLimit(): JSX.Element {
      /**
       * Handle input blur
       * @param {React.FocusEvent<HTMLInputElement>} event
       */
      function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
        const limits = Number(event.target.value) > 0 ? (
          Number(event.target.value)
        ) : undefined;

        formContext.setFieldValue('sale.limits', limits);
        formContext.submitForm();
      }

      return (
        <Row>
          <Column>
            <Row>
              <Column>
                <Input
                  type="number"
                  label="Ограничение (количество человек)"
                  note="Оставьте поле пустым, если без ограничений"
                  defaultValue={courseContext.course?.sale?.limits}
                  placeholder="Без ограничений"
                  onBlur={handleBlur}
                />
              </Column>
            </Row>
          </Column>
        </Row>
      );
    }

    return (
      <React.Fragment>
        <Row>
          <Column>
            {renderDates()}
          </Column>
          <Column>
            {renderDiscount()}
          </Column>
        </Row>
        <Row alignItems="flex-end">
          <Column>
            {renderLimit()}
          </Column>
        </Row>
      </React.Fragment>
    );
  }

  return (
    <FormContainer
      property="sale"
      label="Специальное предложение"
      collapse
      notAvailableForFree
    >
      {renderContent()}
    </FormContainer>
  );
}
